// src/components/Hermes/Tasks/HermesTaskPanel.tsx
// ============================================
// HermesTaskPanel - Hermes 任务调度主面板容器
//
// 设计：
//   - 暗色玻璃拟态卡片（背景 #131826 → #0a0e1a 渐变 + backdrop-blur）
//   - 顶部：标题 + 自动刷新开关 + 手动刷新按钮
//   - Tab 切换：任务列表 / 执行时间线 / 执行日志
//   - 底部：最后更新时间 + 任务统计（总数 / 运行中 / 成功 / 失败）
//
// 数据源：useSchedulerTasks Hook（5s 轮询）
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  CalendarClock,
  ListOrdered,
  ScrollText,
} from "lucide-react";
import { useSchedulerTasks } from "../../../hooks/useSchedulerTasks";
import HermesTaskList from "./HermesTaskList";
import HermesTaskTimeline from "./HermesTaskTimeline";
import HermesTaskLogDrawer from "./HermesTaskLogDrawer";
import { formatTime } from "./types";

type Tab = "list" | "timeline" | "log";

export default function HermesTaskPanel() {
  const [tab, setTab] = useState<Tab>("list");
  // 日志抽屉状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTaskId, setDrawerTaskId] = useState<string | undefined>(undefined);

  const {
    hermesTasks,
    history,
    loading,
    error,
    lastUpdated,
    autoRefresh,
    triggeringTaskIds,
    triggerTask,
    pauseTask,
    resumeTask,
    refresh,
    setAutoRefresh,
    clearHistory,
  } = useSchedulerTasks({ intervalMs: 5000 });

  // 统计：总数 / 运行中 / 成功 / 失败（按最近一次历史判定）
  const stats = {
    total: hermesTasks.length,
    running: hermesTasks.filter((t) => triggeringTaskIds.includes(t.id)).length,
    success: hermesTasks.filter((t) => {
      const h = history.find((h) => h.taskId === t.id);
      return h?.success === true;
    }).length,
    failed: hermesTasks.filter((t) => {
      const h = history.find((h) => h.taskId === t.id);
      return h?.success === false;
    }).length,
  };

  // 打开日志抽屉
  const openLog = (taskId?: string) => {
    setDrawerTaskId(taskId);
    setDrawerOpen(true);
  };

  // 关闭日志抽屉，回退到列表 Tab
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTab("list");
  };

  return (
    <div
      className="relative h-full rounded-xl backdrop-blur-md p-4 flex flex-col overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(19,24,38,0.7) 0%, rgba(10,14,26,0.7) 100%)",
        border: "1px solid rgba(30,42,63,0.8)",
      }}
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />

      {/* 头部：标题 + 控件 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Hermes 任务调度</h3>
          <span className="text-[10px] font-mono text-gray-500">
            {hermesTasks.length} 项
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* 自动刷新开关 */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border transition-colors"
            style={{
              borderColor: autoRefresh
                ? "rgba(59,130,246,0.5)"
                : "rgba(255,255,255,0.1)",
              color: autoRefresh ? "#60A5FA" : "#9CA3AF",
              background: autoRefresh
                ? "rgba(59,130,246,0.1)"
                : "transparent",
            }}
            title={autoRefresh ? "自动刷新开启" : "自动刷新关闭"}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                autoRefresh ? "bg-blue-400" : "bg-gray-500"
              }`}
            />
            {autoRefresh ? "AUTO" : "OFF"}
          </button>
          {/* 手动刷新 */}
          <button
            onClick={() => refresh()}
            disabled={loading}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            title="手动刷新"
          >
            <motion.span
              animate={loading ? { rotate: 360 } : {}}
              transition={{
                duration: 1,
                repeat: loading ? Infinity : 0,
                ease: "linear",
              }}
            >
              <RefreshCw size={13} />
            </motion.span>
          </button>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex items-center gap-1 mb-3 p-1 rounded-lg bg-white/[0.03] border border-white/5">
        {([
          { key: "list" as const, label: "任务列表", icon: ListOrdered },
          { key: "timeline" as const, label: "执行时间线", icon: CalendarClock },
          { key: "log" as const, label: "执行日志", icon: ScrollText },
        ]).map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                if (t.key === "log") openLog(undefined);
              }}
              className="flex-1 flex items-center justify-center gap-1 text-[11px] font-mono py-1.5 rounded-md transition-all"
              style={{
                background: active ? "rgba(59,130,246,0.15)" : "transparent",
                color: active ? "#60A5FA" : "#9CA3AF",
                boxShadow: active ? "0 0 12px rgba(59,130,246,0.2)" : "none",
              }}
            >
              <Icon size={12} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-2 px-2 py-1 rounded text-[10px] font-mono text-red-300 bg-red-500/10 border border-red-500/20">
          ⚠ {error}
        </div>
      )}

      {/* 内容区（flex-1 + 滚动，面板填满可用高度，单一滚动区） */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
        {tab === "list" && (
          <HermesTaskList
            tasks={hermesTasks}
            history={history}
            triggeringTaskIds={triggeringTaskIds}
            onTrigger={triggerTask}
            onPause={pauseTask}
            onResume={resumeTask}
            onViewLog={openLog}
          />
        )}
        {tab === "timeline" && (
          <HermesTaskTimeline
            tasks={hermesTasks}
            history={history}
            onSelect={openLog}
          />
        )}
        {tab === "log" && (
          <div className="text-center py-8 text-[11px] font-mono text-gray-500">
            日志抽屉已打开 →
          </div>
        )}
      </div>

      {/* 底部：最后更新时间 + 统计 */}
      <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-gray-500">
        <span>
          {lastUpdated ? `更新于 ${formatTime(lastUpdated)}` : "未更新"}
        </span>
        <div className="flex items-center gap-3">
          <span>
            共 <span className="text-white">{stats.total}</span>
          </span>
          <span className="text-blue-400">运行 {stats.running}</span>
          <span className="text-emerald-400">成功 {stats.success}</span>
          <span className="text-red-400">失败 {stats.failed}</span>
        </div>
      </div>

      {/* 日志抽屉（始终挂载以支持退出动画） */}
      <HermesTaskLogDrawer
        open={drawerOpen}
        taskId={drawerTaskId}
        onClose={closeDrawer}
        tasks={hermesTasks}
        history={history}
        onFilterChange={setDrawerTaskId}
        onClear={clearHistory}
      />
    </div>
  );
}
